import { type FC, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import type { Number3, ViewerObjectProps } from '../types';
import { ViewerLayer } from './ViewerLayer';
import { useAbyssViewerContext } from './AbyssViewerContext';
import { AutoscaleGroup } from './AutoscaleGroup';

export interface MeasurementLinesStyle {
  color: number;
  opacity: number;
  lineWidth: number;
  lineCap?: 'butt' | 'round' | 'square';
  lineJoin?: 'round' | 'bevel' | 'miter';
  showMarkers?: boolean;
  markerSize?: number;
  markerColor?: number;
  markerOpacity?: number;
  fixedScreenSize?: boolean;
}

export interface MeasurementLinesProps extends ViewerObjectProps {
  points?: Number3[];
  style?: MeasurementLinesStyle;
  setLineDistance?: (distance: number) => void;
}

export const MeasurementLines: FC<MeasurementLinesProps> = ({
  points,
  style,
  isVisible = true,
  setLineDistance,
}) => {
  const { sharedGeometries } = useAbyssViewerContext();
  const markerGeometry = sharedGeometries?.anchorSphere;
  const showMarkers = style?.showMarkers ?? true;
  const fixedScreenSize = style?.fixedScreenSize ?? true;

  const markerMaterial = useMemo(() => {
    if (!showMarkers) return undefined;
    const markerColor = style?.markerColor ?? 0xffffff;
    const markerOpacity = style?.markerOpacity ?? 1.0;
    return new THREE.MeshBasicMaterial({
      color: markerColor,
      opacity: markerOpacity,
      transparent: true,
    });
  }, [showMarkers, style]);
  useEffect(() => {
    return () => markerMaterial?.dispose();
  }, [markerMaterial]);

  const pointVecs = useMemo(() => points?.map((point) => new THREE.Vector3(...point)), [points]);

  const computedLineDistance = useMemo(() => {
    let initialDistance = 0;
    if (pointVecs === undefined) return initialDistance;
    pointVecs.reduce((previousValue, currentValue) => {
      initialDistance += currentValue.distanceTo(previousValue);
      return currentValue;
    });
    return initialDistance;
  }, [pointVecs]);

  const material = useMemo(() => {
    const color = style?.color || 0xffffff;
    const lineWidth = style?.lineWidth || 3;
    const opacity = style?.opacity || 1;
    const lineJoin = style?.lineJoin || 'round';
    const lineCap = style?.lineCap || 'round';
    return new THREE.LineBasicMaterial({
      color,
      linewidth: lineWidth,
      opacity,
      linecap: lineCap,
      linejoin: lineJoin,
    });
  }, [style]);
  useEffect(() => {
    return () => material.dispose();
  }, [material]);

  const geometry = useMemo(() => {
    if (!pointVecs || pointVecs.length < 2) return undefined;
    return new THREE.BufferGeometry().setFromPoints(pointVecs);
  }, [pointVecs]);
  useEffect(() => {
    return () => geometry?.dispose();
  }, [geometry]);

  const lines = useMemo(() => {
    if (!geometry) return undefined;
    return new THREE.Line(geometry, material);
  }, [geometry, material]);

  const layers = useMemo(() => {
    const layers0 = new THREE.Layers();
    if (isVisible) {
      layers0.enable(ViewerLayer.Default);
      layers0.enable(ViewerLayer.SphericalViewerLayer);
    } else {
      layers0.disable(ViewerLayer.Default);
      layers0.disable(ViewerLayer.SphericalViewerLayer);
    }
    return layers0;
  }, [isVisible]);

  useEffect(() => {
    if (!lines) return;
    lines.layers = layers;
  }, [lines, layers]);

  useEffect(() => {
    if (setLineDistance) setLineDistance(computedLineDistance);
  }, [computedLineDistance, setLineDistance]);

  const scale: Number3 = useMemo(() => {
    const markerSize = style?.markerSize ?? (fixedScreenSize ? 5 : 0.02);
    return [markerSize, markerSize, markerSize];
  }, [style?.markerSize, fixedScreenSize]);

  return (
    <>
      {lines && <primitive key="lines" object={lines} />}
      {showMarkers &&
        points?.map((position, index) => {
          return fixedScreenSize ? (
            <AutoscaleGroup position={position} scale={scale} key={index}>
              <mesh
                key={index}
                layers={layers}
                geometry={markerGeometry}
                material={markerMaterial}
              />
            </AutoscaleGroup>
          ) : (
            <mesh
              key={index}
              position={position}
              scale={scale}
              layers={layers}
              geometry={markerGeometry}
              material={markerMaterial}
            />
          );
        })}
    </>
  );
};
